import {gql} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import React, {useEffect, useState} from 'react';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {PipelineStepType, StudyType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {PipelineStepConfig} from 'pipelines/PipelineTypes';
import QueryingProcedureRunSelectorByStudy from 'procedures/shared-components/analysis/ProcedureRunSelectorByStudy';
import {
  ProcedureRunStudyIdStatus,
  useProcedureRunStudyId,
} from 'procedures/shared-components/analysis/useProcedureRunStudyId';
import SearchInput from 'search/inputs/SearchInput';
import {FONT_FAMILY_PROXIMA, FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import {ButtonSize} from 'ui-library/buttons/Button';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import Spinner from 'ui-library/Spinner';
import ToggleGroup from 'ui-library/ToggleGroup';

import {CustomComponentEditorProps} from '../../StepConfigEditorTypes';
import {SeedItemsStudySchemaTypesQuery} from './__generated/SeedItemsStudySchemaTypesQuery';

export const SEED_ITEMS_STUDY_SCHEMA_TYPES_QUERY = gql`
  query SeedItemsStudySchemaTypesQuery @skipAudit @engComponent(name: STUDIES) {
    studySchemas {
      id
      studyType
    }
  }
`;

const ALLOWED_STUDY_TYPES: ReadonlySet<StudyType> = new Set([
  StudyType.ASSAY_DEVELOPMENT,
  StudyType.PROCESS_DEVELOPMENT,
]);

function useAllowedStudySchemaIds(): Array<string> | null {
  const result = useBenchlingQuery<SeedItemsStudySchemaTypesQuery>(SEED_ITEMS_STUDY_SCHEMA_TYPES_QUERY);
  if (result.type !== QueryResultType.SUCCESS) {
    return null;
  }
  return result.data.studySchemas
    .filter((schema) => ALLOWED_STUDY_TYPES.has(schema.studyType))
    .map((schema) => schema.id);
}

type SeedItemType = NonNullable<SeedItems>['seedItemType'];
export type SeedItems = PipelineStepConfig<
  PipelineStepType.CREATE_PROCEDURE_DATASET,
  0,
  'draft'
>['seedItems'];

const DEFAULT_SEED_ITEMS: SeedItems = {seedItemType: 'STUDY', seedItemIds: []};

function getProcedureRunDisplayName(): string {
  return BenchlingConfig.PROCEDURES_ENABLE_ASSAYS ? 'Recipe/Assay runs' : 'Recipe runs';
}

const SEED_ITEM_TYPE_DISPLAY_NAMES: Record<SeedItemType, () => string> = {
  STUDY: () => 'Studies',
  PROCEDURE_RUN: getProcedureRunDisplayName,
  WORKSHEET: () => 'Worksheets',
};

interface SeedItemPickerProps {
  selectedIds: Array<string>;
  onChangeConfig: CustomComponentEditorProps['onChangeConfig'];
  config: CustomComponentEditorProps['config'];
  disabled?: boolean;
  studySchemaIds: Array<string> | null;
}

function StudyPicker({
  selectedIds,
  onChangeConfig,
  config,
  disabled,
  studySchemaIds,
}: SeedItemPickerProps): JSX.Element {
  const isDisabled = disabled || studySchemaIds == null;

  const handleSelect = (studyId: string | null): void => {
    if (studyId == null) {
      return;
    }
    onChangeConfig(config, ['seedItems', 'seedItemIds'], [studyId]);
  };

  const handleRemove = (): void => {
    onChangeConfig(config, ['seedItems', 'seedItemIds'], []);
  };

  return (
    <SearchInput
      autoFocus={false}
      disabled={isDisabled}
      disableTyping={selectedIds.length > 0}
      isMulti={false}
      maxItems={1}
      hideSelectedResults={true}
      onRemove={handleRemove}
      onSelect={handleSelect}
      placeholder="Select a study"
      selected={selectedIds}
      schemaIds={studySchemaIds ?? undefined}
      types={[ModelTypeConstants.STUDY]}
    />
  );
}

function ProcedureRunPicker({
  selectedIds,
  onChangeConfig,
  config,
  disabled,
  studySchemaIds,
}: SeedItemPickerProps): JSX.Element {
  const [scopingStudyIds, setScopingStudyIds] = useState<Array<string>>([]);

  // Assumes all procedure run IDs in a saved config belong to the same study.
  // We fetch the study from the first run ID to pre-populate the scoping study selector.
  const needsStudyFetch = selectedIds.length > 0 && scopingStudyIds.length === 0;
  const firstProcedureRunId = needsStudyFetch ? selectedIds[0] : null;
  const studyIdResult = useProcedureRunStudyId(firstProcedureRunId);

  const isStudyLoading = studyIdResult.status === ProcedureRunStudyIdStatus.PENDING;
  const fetchedStudyId =
    studyIdResult.status === ProcedureRunStudyIdStatus.SUCCESS ? studyIdResult.studyId : null;

  useEffect(() => {
    if (selectedIds.length > 0 && scopingStudyIds.length === 0 && fetchedStudyId != null) {
      setScopingStudyIds([fetchedStudyId]);
    }
  }, [selectedIds.length, scopingStudyIds.length, fetchedStudyId]);

  const effectiveScopingStudyIds =
    scopingStudyIds.length > 0 ? scopingStudyIds : fetchedStudyId != null ? [fetchedStudyId] : [];
  const scopingStudyId = effectiveScopingStudyIds.length === 1 ? effectiveScopingStudyIds[0] : null;
  const noStudySelected = effectiveScopingStudyIds.length === 0;

  const handleScopingStudySelect = (studyId: string | null): void => {
    if (studyId == null) {
      return;
    }
    setScopingStudyIds([studyId]);
    onChangeConfig(config, ['seedItems', 'seedItemIds'], []);
  };

  const handleScopingStudyRemove = (): void => {
    setScopingStudyIds([]);
    onChangeConfig(config, ['seedItems', 'seedItemIds'], []);
  };

  const handleRunSelect = (runId: string | null): void => {
    if (runId == null) {
      return;
    }
    onChangeConfig(config, ['seedItems', 'seedItemIds'], [...selectedIds, runId]);
  };

  const handleRunRemove = (runId: string | null): void => {
    if (runId == null) {
      return;
    }
    onChangeConfig(
      config,
      ['seedItems', 'seedItemIds'],
      selectedIds.filter((id) => id !== runId)
    );
  };

  const runTypeName = BenchlingConfig.PROCEDURES_ENABLE_ASSAYS ? 'recipe or assay run' : 'recipe run';
  const runPlaceholder = noStudySelected ? 'Select a study first' : `Select ${runTypeName}`;
  const isRunSelectorDisabled = disabled || noStudySelected;

  const runSelector = (
    <QueryingProcedureRunSelectorByStudy
      studyId={scopingStudyId}
      selectedRunIds={selectedIds}
      onSelect={handleRunSelect}
      onRemove={handleRunRemove}
      disabled={isRunSelectorDisabled}
      placeholder={runPlaceholder}
    />
  );

  return (
    <>
      <div className={css(styles.infoText)}>Select a study, then choose runs from that study</div>
      <div className={css(styles.searchContainer)}>
        {isStudyLoading ? (
          <div className={css(styles.loadingContainer)}>
            <Spinner />
          </div>
        ) : (
          <SearchInput
            autoFocus={false}
            disabled={disabled || studySchemaIds == null}
            disableTyping={effectiveScopingStudyIds.length > 0}
            isMulti={false}
            maxItems={1}
            hideSelectedResults={true}
            onRemove={handleScopingStudyRemove}
            onSelect={handleScopingStudySelect}
            placeholder="Select a study"
            selected={effectiveScopingStudyIds}
            schemaIds={studySchemaIds ?? undefined}
            types={[ModelTypeConstants.STUDY]}
          />
        )}
      </div>
      {!isStudyLoading && (
        <div className={css(styles.searchContainer)}>
          <Tooltip
            text="Select a study first"
            placement={FloatingPlacement.TOP}
            isDisabled={!noStudySelected}
          >
            <div>{runSelector}</div>
          </Tooltip>
        </div>
      )}
    </>
  );
}

function WorksheetPicker({selectedIds, onChangeConfig, config, disabled}: SeedItemPickerProps): JSX.Element {
  const handleSelect = (worksheetId: string | null): void => {
    if (worksheetId == null) {
      return;
    }
    onChangeConfig(config, ['seedItems', 'seedItemIds'], [worksheetId]);
  };

  const handleRemove = (): void => {
    onChangeConfig(config, ['seedItems', 'seedItemIds'], []);
  };

  return (
    <SearchInput
      autoFocus={false}
      disabled={disabled}
      disableTyping={selectedIds.length > 0}
      isMulti={false}
      maxItems={1}
      hideSelectedResults={true}
      onRemove={handleRemove}
      onSelect={handleSelect}
      placeholder="Select a worksheet"
      selected={selectedIds}
      types={[ModelTypeConstants.WORKSHEET]}
    />
  );
}

function getSeedItemPicker(type: SeedItemType): (props: SeedItemPickerProps) => JSX.Element {
  switch (type) {
    case 'STUDY':
      return StudyPicker;
    case 'PROCEDURE_RUN':
      return ProcedureRunPicker;
    case 'WORKSHEET':
      return WorksheetPicker;
    default:
      assert(false, 'Got unexpected seed item type on', {type});
  }
}

export default function SeedItemsEditor({
  config,
  onChangeConfig,
  disabled,
}: CustomComponentEditorProps): JSX.Element {
  const seedItems = (config.seedItems as NonNullable<SeedItems>) ?? DEFAULT_SEED_ITEMS;
  const currentType = seedItems.seedItemType;
  const currentIds = seedItems.seedItemIds.filter((id): id is string => id != null);
  const studySchemaIds = useAllowedStudySchemaIds();

  // If the seed items in the config are not set, set them to the default.
  useEffect(() => {
    if (config.seedItems == null) {
      onChangeConfig(config, ['seedItems'], DEFAULT_SEED_ITEMS);
    }
  }, [config, onChangeConfig, seedItems]);

  const handleTypeChange = (newType: string): void => {
    if (newType === currentType) {
      return;
    }
    onChangeConfig(config, ['seedItems'], {
      seedItemType: newType,
      seedItemIds: [],
    });
  };

  const toggleOptions = Object.entries(SEED_ITEM_TYPE_DISPLAY_NAMES).map(([type, displayName]) => ({
    id: type,
    buttonProps: {text: displayName()},
  }));

  const pickerProps: SeedItemPickerProps = {
    selectedIds: currentIds,
    onChangeConfig,
    config,
    disabled,
    studySchemaIds,
  };

  const SeedItemPicker = getSeedItemPicker(currentType);

  return (
    <div className={css(styles.container)}>
      <div className={css(styles.sectionTitle)}>Include data from</div>
      <div className={css(styles.toggleGroupContainer)}>
        <ToggleGroup
          options={toggleOptions}
          selection={currentType}
          onChangeSelection={handleTypeChange}
          size={ButtonSize.MEDIUM}
          disabled={disabled}
          isFullWidth={true}
        />
      </div>
      <div className={css(styles.searchContainer)}>
        <SeedItemPicker {...pickerProps} />
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    width: '100%',
    marginTop: 16,
  },
  sectionTitle: {
    fontFamily: FONT_FAMILY_PROXIMA,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    fontSize: 13,
    lineHeight: '16px',
    letterSpacing: 0,
  },
  toggleGroupContainer: {
    display: 'flex',
    justifyContent: 'center',
  },
  searchContainer: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 36,
  },
  infoText: {
    fontSize: 12,
    color: 'var(--text-secondary)',
  },
});
